import React, { useState, useEffect, useCallback } from "react"
import Checkbox from "aldoo-ra/Checkbox"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Typography from "aldoo-ra/Typography"

export default function BooleanEditor({
  onChange,
  value,
  field,
  parentObject,
}) {
  const normalizeBoolean = (val) => {
    if (typeof val === "string") return val === "true"
    return !!val
  }

  const defaultValue =
    field.default !== undefined ? normalizeBoolean(field.default) : false

  const [localValue, setLocalValue] = useState(
    value === undefined || value === "" ? defaultValue : normalizeBoolean(value)
  )

  // Call onChange with initial value after mount — only when there is a
  // real value or a configured default. Otherwise an instance that has not
  // loaded yet would be seeded with `false`, and that would be saved over
  // a stored `true` (same guard as the text editor).
  useEffect(() => {
    if ((value === undefined || value === "") && field.default === undefined) return
    const initialValue =
      value === undefined || value === ""
        ? defaultValue
        : normalizeBoolean(value)
    onChange?.(initialValue)
  }, []) // Empty dependency array ensures this only runs once after mount

  const isRequired =
    typeof field.required === "function"
      ? field.required(parentObject)
      : field.required

  useEffect(() => {
    const newValue =
      value === undefined || value === ""
        ? defaultValue
        : normalizeBoolean(value)
    if (newValue !== localValue) {
      setLocalValue(newValue)
    }
  }, [value, defaultValue, localValue])

  // Memoize the handler to prevent unnecessary re-renders
  const handleCheckboxChange = useCallback(
    (isChecked) => {
      setLocalValue(isChecked)
      if (onChange) {
        onChange(isChecked)
      }
    },
    [onChange]
  )

  // Create a single handler for the container click
  const handleContainerClick = useCallback(
    (e) => {
      // Prevent double triggers if the click is on the actual checkbox
      if (e.target.closest(".checkbox-control")) {
        return
      }
      handleCheckboxChange(!localValue)
    },
    [handleCheckboxChange, localValue]
  )

  // a field may ask to be a switch rather than a checkbox: `render.variant`
  // "toggle" — for the flags that read as on/off rather than as yes/no
  if (field.render?.variant === "toggle") {
    return (
      <div className="flex items-center gap-3">
        <ToggleSwitch variant="admin" isOn={localValue} onChange={handleCheckboxChange} />
        <Typography variant="admin_txt" className="text-admin_text_secondary">
          {localValue ? "On" : "Off"}
        </Typography>
      </div>
    )
  }

  return (
    // Add role="button" and tabIndex for accessibility
    <div
      onClick={handleContainerClick}
      role="button"
      tabIndex={0}
      className="cursor-pointer"
      // Add keyboard support
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          handleContainerClick(e)
        }
      }}
    >
      <Checkbox
        variant="admin"
        className="checkbox-control" // Add this class to help prevent double triggers
        label={localValue ? "Yes" : "No"}
        isOn={localValue}
        onChange={handleCheckboxChange}
      />
    </div>
  )
}
